import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Key } from '@phosphor-icons/react';
import { api } from '~/lib/api-client';
import { Button } from '~/components/ui/button';
import { cronKeys } from '~/lib/query-keys';
import { toast } from 'sonner';

/**
 * Bulk re-auth for every cron this user owns (FRONT-1819).
 *
 * The Security tab held this next to an Authed/Expired/Missing tri-stat. The
 * stat did not come with it: this page already badges each cron's auth state
 * per row and the sidebar already counts the ones needing attention
 * (`cronsNeedingAuth`), so the aggregate was a third telling of the same
 * number. The BUTTON is the part nothing else offered — re-authing twenty
 * jobs one row at a time is the reason it exists.
 *
 * Invalidates `cronKeys.authStatus()` explicitly, not just the list: that key
 * is what drives both the per-row badges here AND the sidebar's amber alert,
 * so without it a successful bulk re-auth would leave every "needs auth"
 * badge and the sidebar count exactly as they were.
 */
export function useReauthAllCrons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ updated: number }>('/api/security/reauth-all-crons'),
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: cronKeys.authStatus() });
      qc.invalidateQueries({ queryKey: cronKeys.list() });
      toast.success(`Re-authed ${resp.updated} cron job${resp.updated === 1 ? '' : 's'}`);
    },
    onError: (err) => toast.error(`Re-auth failed: ${(err as Error).message}`),
  });
}

export function ReauthAllCronsButton({ disabled }: { disabled?: boolean }) {
  // FRONT-2363: the mutation is shared with the bell's re-auth item, so the
  // endpoint keeps one home (FRONT-1819's single-home guard).
  const reauthAll = useReauthAllCrons();

  return (
    <Button
      variant="outline"
      onClick={() => reauthAll.mutate()}
      disabled={disabled || reauthAll.isPending}
      tooltip="Refresh the stored tokens on every job you own, from your current session"
    >
      <Key size={14} /> Re-auth all
    </Button>
  );
}
